import React, { useCallback, useState } from 'react';
import { Button } from '@asigna/kit';
import { addContact, editContact } from 'api';
import { NotificationCard } from 'components/NotificationCard';
import { useAtom } from 'jotai/index';
import { isEmpty } from 'lodash';
import { debounce } from 'lodash';
import { resolveBnsName, resolveStxsName } from 'pages/AssetsPage/components/Stx20Tab/utils';
import { toast } from 'sonner';

import useAddress from '../../../hooks/use-address';
import useNetwork from '../../../hooks/use-network';
import { contactsAtom } from '../../../store/contacts';
import { IContact } from '../../../types/contacts';
import { isValidRecipient } from '../../../utils';
import { InputField } from '../../InputField/InputField';
import { TransactionCommonDialog } from '../TransactionDialog/TransactionCommonDialog';

function shortenString(input: string) {
  return input.length <= 12
    ? input
    : `${input.substring(0, 4)}...${input.substring(input.length - 8)}`;
}

interface AddContactProps {
  open: boolean;
  onClose: (refresh: boolean) => void;
  // TODO_R: remove this prop, cuz it is not used
  setLoading?: (loading: boolean) => void;
  editingContact?: IContact | null;
  fetchContacts: () => void;
  presetWallet?: string;
}

// TODO_R: [RP] rename to AddContactDialog
export const AddContact = (props: AddContactProps) => {
  const [contacts] = useAtom(contactsAtom);
  const { open, onClose, editingContact, fetchContacts, presetWallet } = props;
  const [name, setName] = useState(editingContact?.contact_name || '');
  const [nameError, setNameError] = useState('');
  const [address, setAddress] = useState(presetWallet || editingContact?.contact_address || '');
  const [addressError, setAddressError] = useState('');
  const walletAccount = useAddress();
  const [network] = useNetwork();
  const [editingName, setEditingName] = useState<string | null>(null);
  const [bnsContent, setBnsContent] = useState<string | null>('');
  const [isChanged, setChanged] = useState(false);

  const handleInputChange = (event: any) => {
    setEditingName(event.target.value);
  };

  const checkNameValidity = (name: string) => {
    if (isEmpty(name)) {
      setNameError('Invalid name');

      return false;
    }
    if (
      contacts.some(
        (contact) =>
          name === contact.contact_name &&
          ((editingContact && editingContact._id !== contact._id) || !editingContact)
      )
    ) {
      setNameError('This name is already added');

      return false;
    }

    return true;
  };

  const checkDuplicateAddress = (address: string) => {
    if (contacts.some((contact) => contact.contact_address === address)) {
      setAddressError('This address is already added');

      return false;
    }

    return true;
  };

  const checkAddressValidity = (address: string) => {
    if (!contacts) return false;
    setAddressError('');
    const isBns = address.includes('.');
    if (!isBns) {
      if (!isEmpty(address) && !isValidRecipient(address)) {
        setAddressError('Invalid address');

        return false;
      }
      if (!checkDuplicateAddress(address)) return false;
    } else {
      setBnsContent(null);
      if (address.endsWith('.stxs')) {
        resolveStxsName(address, network).then((res) => {
          if (res) {
            setBnsContent(res);
            if (!checkDuplicateAddress(res)) return false;
          } else {
            setAddressError('Invalid STXS name');
          }
        });
      } else {
        resolveBnsName(address).then((res) => {
          if (res) {
            setBnsContent(res);
            if (!checkDuplicateAddress(res)) return false;
          } else {
            setAddressError('Invalid BNS name');
          }
        });
      }
    }

    return true;
  };

  const debounceCheckAddress = useCallback(debounce(checkAddressValidity, 300), []);

  const handleSave = async () => {
    if (isEmpty(walletAccount)) {
      onClose(false);

      return;
    }

    if (!checkNameValidity(name)) {
      return;
    }

    if (!editingContact && !checkAddressValidity(address)) {
      return;
    }
    if (editingContact && editingContact?._id) {
      editContact(name, bnsContent ? bnsContent : address, editingContact?._id).then(() => {
        fetchContacts();
        toast(
          () => (
            <NotificationCard
              title="Contact Edited"
              message={`Contact <b>${name}</b> edited successfully.`}
              type="success"
            />
          ),
          {
            className: 'w-full',
          }
        );
        onClose(false);
      });
    } else {
      addContact(name, bnsContent ? bnsContent : address).then(() => {
        fetchContacts();
        toast(
          () => (
            <NotificationCard
              title="Contact Added"
              message={`New contact <b>${name}</b> added successfully.`}
              type="success"
            />
          ),
          {
            className: 'w-full',
          }
        );
        onClose(false);
      });
    }
  };

  const isValid = () => {
    return isEmpty(nameError) && isEmpty(addressError) && address.length > 0;
  };

  return (
    <TransactionCommonDialog
      open={open}
      onClose={() => onClose(false)}
      title={Boolean(editingContact) ? 'Edit contact' : 'Add new address'}
      button={
        <Button
          fullWidth
          disabled={!(isValid() && isChanged)}
          onClick={handleSave}
          message="Save"
          colorType="primary"
        />
      }
    >
      <div className="w-full mt-32 mb-32">
        <div className="relative mb-16">
          <InputField
            label="Name"
            value={editingName ?? name}
            placeHolder=""
            type="text"
            onChange={(e) => {
              setNameError('');
              setChanged(true);
              setName(e.target.value);
              checkNameValidity(e.target.value);
              handleInputChange(e);
            }}
            errorInfo={nameError}
          />
        </div>

        <InputField
          label="Address or BNS name"
          value={address}
          placeHolder=""
          type="text"
          onChange={(e) => {
            setAddressError('');
            setChanged(true);
            setAddress(e.target.value);
            // checkAddressValidity(e.target.value);
            debounceCheckAddress(e.target.value);
          }}
          errorInfo={addressError}
          bnsAddress={bnsContent ? shortenString(bnsContent) : ''}
        />
      </div>
    </TransactionCommonDialog>
  );
};
